import React, { useMemo } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SarIcon from "../icons/SarIcon";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DonutLargeIcon from "@mui/icons-material/DonutLarge";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { BarChart } from "@mui/x-charts/BarChart";
import { useNavigate } from "react-router-dom";
import { useGetInvestorPayoutsQuery } from "../../redux/api/authApi";
import { resolveInvestorUploadUrl } from "./myInvestmentsMapper";

const cardSx = {
  p: 3,
  borderRadius: 1.5,
  boxShadow: (theme) => theme.palette.shadow.statCard,
  border: (theme) => `1px solid ${theme.palette.border.light}`,
  bgcolor: "background.paper",
};

function formatSar(n) {
  const x = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(x)) return "—";
  return new Intl.NumberFormat("en-SA", { maximumFractionDigits: 2 }).format(x);
}

function formatTokens(n) {
  const x = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(x)) return "0";
  return new Intl.NumberFormat("en-US").format(Math.round(x));
}

function formatPayoutDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-SA", { year: "numeric", month: "short", day: "numeric" });
}

const StatCard = ({ icon, label, value }) => (
  <Paper elevation={0} sx={cardSx}>
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 1.25,
        bgcolor: "background.subtle",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 2,
      }}
    >
      {React.cloneElement(icon, {
        sx: {
          color: (theme) => theme.palette.text.muted,
          fontSize: 22,
        },
      })}
    </Box>
    <Typography variant="body2" sx={{ color: "text.muted" }}>
      {label}
    </Typography>
    <Typography
      variant="h5"
      fontWeight={700}
      sx={{ color: "text.primary", mt: 0.5, letterSpacing: -0.5 }}
    >
      {value}
    </Typography>
  </Paper>
);

const TYPE_CHIP_KEY = {
  Dividend: "info",
  Rental: "success",
  "Rental Income": "success",
  "Revenue Share": "violet",
  "Storage Fee Rebate": "pending",
  "Exhibition Revenue": "neutral",
};

const TypeChip = ({ label }) => {
  const key = TYPE_CHIP_KEY[label] || "neutral";
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        bgcolor: (theme) => theme.palette.chips[key].bg,
        color: (theme) => theme.palette.chips[key].text,
        fontWeight: 600,
        fontSize: 11,
        height: 24,
        borderRadius: 999,
      }}
    />
  );
};

const STATUS_CHIP_KEY = {
  Paid: "success",
  Scheduled: "pending",
};

const StatusPill = ({ label }) => {
  const key = STATUS_CHIP_KEY[label] || "neutral";
  const isPaid = String(label).toLowerCase() === "paid";
  return (
    <Chip
      icon={
        isPaid ? (
          <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
        ) : (
          <ScheduleIcon sx={{ fontSize: 14 }} />
        )
      }
      label={label}
      size="small"
      sx={{
        bgcolor: (theme) => theme.palette.chips[key].bg,
        color: (theme) => theme.palette.chips[key].text,
        fontWeight: 600,
        fontSize: 11,
        height: 24,
        borderRadius: 999,
        "& .MuiChip-icon": {
          color: (theme) => theme.palette.chips[key].text,
        },
      }}
    />
  );
};

const InvestorIncome = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [tab, setTab] = React.useState("history");
  const [category, setCategory] = React.useState("All Categories");
  const [sortBy, setSortBy] = React.useState("Date (Newest)");
  const [search, setSearch] = React.useState("");
  const [historyPage, setHistoryPage] = React.useState(1);
  const [upcomingPage, setUpcomingPage] = React.useState(1);
  const [limit, setLimit] = React.useState(10);

  const page = tab === "upcoming" ? upcomingPage : historyPage;

  React.useEffect(() => {
    setHistoryPage(1);
    setUpcomingPage(1);
  }, [search, category, sortBy]);

  const queryArgs = useMemo(
    () => ({
      tab,
      search: search.trim(),
      category,
      sortBy,
      page,
      limit,
    }),
    [tab, search, category, sortBy, page, limit]
  );

  const { data: payoutsRes, isLoading, isFetching, isError, error, refetch } =
    useGetInvestorPayoutsQuery(queryArgs, { refetchOnMountOrArgChange: true });

  const payoutsData = payoutsRes?.data?.data ?? payoutsRes?.data ?? {};
  const overview = payoutsData.overview ?? {};
  const trend = Array.isArray(payoutsData.trend) ? payoutsData.trend : [];
  const historyItems = Array.isArray(payoutsData.history?.items)
    ? payoutsData.history.items
    : [];
  const upcomingItems = Array.isArray(payoutsData.upcoming?.items)
    ? payoutsData.upcoming.items
    : [];

  const activeItems = tab === "upcoming" ? upcomingItems : historyItems;
  const activePagination =
    tab === "upcoming" ? payoutsData.upcoming?.pagination : payoutsData.history?.pagination;
  const total = Number(activePagination?.total ?? 0) || 0;

  const handlePageChange = (_, newPage) => {
    const nextPage = newPage + 1;
    if (tab === "upcoming") setUpcomingPage(nextPage);
    else setHistoryPage(nextPage);
  };

  const handleRowsPerPageChange = (e) => {
    const nextLimit = parseInt(e.target.value, 10);
    setLimit(nextLimit);
    setHistoryPage(1);
    setUpcomingPage(1);
  };

  const categoryOptions = useMemo(() => {
    const set = new Set();
    [...historyItems, ...upcomingItems].forEach((row) => {
      const cat = row?.asset?.category;
      if (cat) set.add(cat);
    });
    return ["All Categories", ...Array.from(set).sort()];
  }, [historyItems, upcomingItems]);

  const trendMonths = trend.map((t) => t?.month ?? t?.label ?? "");
  const trendValues = trend.map((t) => Number(t?.amount ?? t?.value ?? 0));

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
        onClick={() => navigate("/investor/dashboard")}
        sx={{
          textTransform: "none",
          color: "text.primary",
          mb: 2,
          px: 0,
          fontWeight: 600,
          "&:hover": { backgroundColor: "transparent" },
        }}
      >
        Back to Dashboard
      </Button>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 1.5,
              bgcolor: "background.headerIcon",
              border: (theme) => `1px solid ${theme.palette.border.light}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SarIcon sx={{ color: "text.slate", fontSize: 28 }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ color: "text.primary", letterSpacing: -0.5 }}
            >
              Income &amp; Distributions
            </Typography>
            <Typography variant="body2" sx={{ color: "text.muted", mt: 0.5 }}>
              Track earnings across all tokenized assets
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 1.25,
            borderColor: "border.light",
            color: "text.primary",
            bgcolor: "common.white",
            px: 2.5,
            py: 1,
            "&:hover": {
              bgcolor: "common.white",
              borderColor: "border.light",
            },
          }}
        >
          Export Report
        </Button>
      </Stack>

      {isError ? (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => refetch()}>
          {error?.data?.message || error?.error || "Could not load payouts."}
        </Alert>
      ) : null}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={<AutoAwesomeIcon />}
            label="Total Income Earned"
            value={
              isLoading
                ? "—"
                : `SAR ${formatSar(overview.totalIncomeEarned)}`
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={<CalendarTodayIcon />}
            label="This Month"
            value={
              isLoading ? "—" : `SAR ${formatSar(overview.thisMonth)}`
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={<AccessTimeIcon />}
            label="Next Payouts (Est.)"
            value={
              isLoading
                ? "—"
                : `SAR ${formatSar(overview.nextPayoutsEst)}`
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={<DonutLargeIcon />}
            label="Active Streams"
            value={
              isLoading
                ? "—"
                : `${overview.activeStreams ?? 0} Assets`
            }
          />
        </Grid>
      </Grid>

      {trend.length > 0 ? (
        <Paper elevation={0} sx={{ ...cardSx, mb: 3 }}>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{ color: "text.primary", mb: 2 }}
          >
            Income Trend
          </Typography>
          <Box sx={{ width: "100%", height: 280 }}>
            <BarChart
              xAxis={[{ scaleType: "band", data: trendMonths }]}
              yAxis={[
                {
                  valueFormatter: (v) => `${(v / 1000).toFixed(1)}k`,
                },
              ]}
              series={[
                {
                  data: trendValues,
                  color: theme.palette.accent.teal.chipText,
                },
              ]}
              margin={{ top: 10, bottom: 30, left: 40, right: 10 }}
              slotProps={{ legend: { hidden: true } }}
            />
          </Box>
        </Paper>
      ) : null}

      <Paper elevation={0} sx={{ ...cardSx, p: 0, mb: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          spacing={2}
          sx={{ p: 2.5 }}
        >
          <Stack direction="row" spacing={1}>
            <Button
              onClick={() => setTab("history")}
              sx={{
                textTransform: "none",
                px: 2.5,
                py: 1,
                borderRadius: 1.25,
                fontWeight: 700,
                bgcolor: (theme) =>
                  tab === "history" ? theme.palette.background.subtle : "transparent",
                color: tab === "history" ? "text.primary" : "text.muted",
                "&:hover": {
                  bgcolor: (theme) => theme.palette.background.subtle,
                },
              }}
            >
              Payout History
            </Button>
            <Button
              onClick={() => setTab("upcoming")}
              sx={{
                textTransform: "none",
                px: 2.5,
                py: 1,
                borderRadius: 1.25,
                fontWeight: 700,
                bgcolor: (theme) =>
                  tab === "upcoming" ? theme.palette.background.subtle : "transparent",
                color: tab === "upcoming" ? "text.primary" : "text.muted",
                "&:hover": {
                  bgcolor: (theme) => theme.palette.background.subtle,
                },
              }}
            >
              Upcoming Schedule
            </Button>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search assets..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                width: 220,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 999,
                  bgcolor: "background.subtle",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.muted" }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <FilterListIcon fontSize="small" sx={{ color: "text.muted" }} />
                  </InputAdornment>
                }
                sx={{ borderRadius: 999 }}
              >
                {categoryOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt === "All Categories" ? "All Categories" : opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <SwapVertIcon fontSize="small" sx={{ color: "text.muted" }} />
                  </InputAdornment>
                }
                sx={{ borderRadius: 999 }}
              >
                <MenuItem value="Date (Newest)">Date (Newest)</MenuItem>
                <MenuItem value="Date (Oldest)">Date (Oldest)</MenuItem>
                <MenuItem value="Amount (High)">Amount (High)</MenuItem>
                <MenuItem value="Amount (Low)">Amount (Low)</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>

        {isLoading || isFetching ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: (theme) => theme.palette.accent.teal.chipText }} />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: (theme) => theme.palette.table.headBg }}>
                <TableRow>
                  {[
                    "Asset Details",
                    "Type",
                    "Period",
                    "Tokens",
                    "Amount",
                    "Date",
                    "Status",
                    "Action",
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: (theme) => theme.palette.table.headText,
                        py: 2,
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {activeItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, color: "text.muted" }}>
                      {tab === "upcoming"
                        ? "No upcoming payouts scheduled."
                        : "No payout history yet."}
                    </TableCell>
                  </TableRow>
                ) : (
                  activeItems.map((row) => {
                    const asset = row?.asset ?? {};
                    const imageUrl = resolveInvestorUploadUrl(asset.imageUrl);
                    const currency = row?.currency || "SAR";
                    const assetId = asset.assetId || asset._id;

                    return (
                      <TableRow
                        key={row._id}
                        hover
                        sx={{
                          "&:hover": {
                            bgcolor: (theme) => theme.palette.table.rowHover,
                          },
                          "& td": {
                            borderColor: (theme) => theme.palette.border.subtle,
                          },
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Avatar
                              variant="rounded"
                              src={imageUrl || undefined}
                              alt={asset.name || "Asset"}
                              sx={{
                                width: 36,
                                height: 36,
                                bgcolor: "background.subtle",
                                color: (theme) => theme.palette.accent.teal.chipText,
                                fontSize: 13,
                                fontWeight: 700,
                                border: (theme) => `1px solid ${theme.palette.border.light}`,
                              }}
                            >
                              {(asset.name || "?").charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography
                                fontSize={14}
                                fontWeight={700}
                                sx={{ color: "text.primary" }}
                              >
                                {asset.name || "—"}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "text.muted" }}
                              >
                                {asset.category || "—"}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <TypeChip label={row.type || "—"} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 13, color: "text.primary" }}>
                          {row.period || "—"}
                        </TableCell>
                        <TableCell
                          sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}
                        >
                          {formatTokens(row.tokens)}
                        </TableCell>
                        <TableCell
                          sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}
                        >
                          {currency} {formatSar(row.amount)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 13, color: "text.muted" }}>
                          {formatPayoutDate(row.date)}
                        </TableCell>
                        <TableCell>
                          <StatusPill label={row.status || "—"} />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            sx={{ color: "text.muted" }}
                            aria-label="Open asset"
                            disabled={!assetId}
                            onClick={() =>
                              assetId &&
                              navigate(`/investor/viewinvest/${assetId}?managed=1`, {
                                state: { viewTab: "my" },
                              })
                            }
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!isLoading && !isFetching ? (
          <TablePagination
            component="div"
            count={Math.max(total, activeItems.length)}
            page={Math.max(0, page - 1)}
            onPageChange={handlePageChange}
            rowsPerPage={limit}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Rows per page"
            sx={{
              px: 2.5,
              pb: 2,
              borderTop: (theme) => `1px solid ${theme.palette.border.subtle}`,
              ".MuiTablePagination-toolbar": { px: 0 },
            }}
          />
        ) : null}
      </Paper>
    </Box>
  );
};

export default InvestorIncome;
